import React, { useState } from "react";
import { styled } from "@glitz/react";
import { black, fontSize, fontWeight, green, spacing, Theme } from "../theme";
import Input from "./basic/TextInput";
import Button from "./basic/Button";

interface BackInStockFormProps {
  productId: number | string;
  productName: string;
  variationId?: number | string;
  variationLabel?: string;
}

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const BackInStockForm: React.FC<BackInStockFormProps> = ({
  productId,
  productName,
  variationId,
  variationLabel,
}) => {
  const [email, setEmail] = useState<string>("");
  const [website, setWebsite] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;

    if (!email.trim()) {
      setError("Ange e-post");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Ange en giltig e-postadress");
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const response = await fetch("/api/back-in-stock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          productId,
          productName,
          variationId,
          variationLabel,
          productUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
          website,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Kunde inte registrera notisen");
      }
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Något gick fel – försök igen senare"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Wrapper>
        <SuccessText>
          Tack! Vi hör av oss på <strong>{email.trim()}</strong> när varan är
          tillbaka i lager.
        </SuccessText>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading>Få notis när varan finns i lager</Heading>
      <Description>
        Lämna din e-post så hör vi av oss så snart{" "}
        <strong>{productName}</strong>
        {variationLabel ? ` (${variationLabel})` : ""} finns att köpa igen.
      </Description>
      <Form onSubmit={handleSubmit} noValidate>
        <Input
          label="E-post"
          type="email"
          placeholder="din@epost.se"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          error={Boolean(error)}
          helpText={error}
        />
        <Honeypot
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
        <Button
          as={Theme.Dark}
          type="submit"
          disabled={submitting}
          onClick={() => handleSubmit()}
        >
          {submitting ? "Skickar…" : "Notifiera mig"}
        </Button>
      </Form>
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  padding: { xy: spacing.m },
  marginTop: spacing.s,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "6px",
});

const Heading = styled.h3({
  margin: { xy: 0 },
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
});

const Description = styled.p({
  margin: { xy: 0 },
  fontSize: fontSize.s,
  color: black,
});

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const SuccessText = styled.p({
  margin: { xy: 0 },
  fontSize: fontSize.s,
  color: green,
  fontWeight: fontWeight.bold,
});

const Honeypot = styled.input({
  position: "absolute",
  left: "-9999px",
  width: "1px",
  height: "1px",
  opacity: 0,
});

export default BackInStockForm;
